import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const RankingTable = ({ buildings }) => {
  const [sortBy, setSortBy] = useState('score');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const sortedBuildings = [...buildings]?.sort((a, b) => {
    const multiplier = sortOrder === 'asc' ? 1 : -1;
    return (a?.[sortBy] - b?.[sortBy]) * multiplier;
  });

  const getRankBadge = (rank) => {
    const badges = {
      1: { bg: 'bg-success/10', text: 'text-success', icon: 'Trophy' },
      2: { bg: 'bg-primary/10', text: 'text-primary', icon: 'Medal' },
      3: { bg: 'bg-warning/10', text: 'text-warning', icon: 'Award' }
    };
    return badges?.[rank] || { bg: 'bg-muted', text: 'text-muted-foreground', icon: 'Building' };
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">Building Rankings</h3>
          <p className="caption text-muted-foreground">Performance scores and trends</p>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="ArrowUpDown" size={16} className="text-muted-foreground" />
          <span className="caption text-muted-foreground">Sort by: {sortBy}</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2 text-sm font-semibold text-foreground">Rank</th>
              <th className="text-left py-3 px-2 text-sm font-semibold text-foreground">Building</th>
              <th
                className="text-right py-3 px-2 text-sm font-semibold text-foreground cursor-pointer hover:text-primary transition-smooth"
                onClick={() => handleSort('score')}
              >
                <div className="flex items-center justify-end gap-1">
                  Score
                  <Icon name="ChevronsUpDown" size={14} />
                </div>
              </th>
              <th
                className="text-right py-3 px-2 text-sm font-semibold text-foreground cursor-pointer hover:text-primary transition-smooth"
                onClick={() => handleSort('occupancy')}
              >
                <div className="flex items-center justify-end gap-1">
                  Occupancy
                  <Icon name="ChevronsUpDown" size={14} />
                </div>
              </th>
              <th className="text-center py-3 px-2 text-sm font-semibold text-foreground">Trend</th>
            </tr>
          </thead>
          <tbody>
            {sortedBuildings?.map((building, index) => {
              const rank = index + 1;
              const badge = getRankBadge(rank);
              return (
                <tr key={building?.id} className="border-b border-border hover:bg-muted/50 transition-smooth">
                  <td className="py-3 px-2">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${badge?.bg}`}>
                      <Icon name={badge?.icon} size={16} className={badge?.text} />
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <div>
                      <p className="text-sm font-medium text-foreground">{building?.name}</p>
                      <p className="caption text-muted-foreground">{building?.totalRooms} rooms</p>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className="text-sm font-semibold data-text text-foreground">{building?.score}</span>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className="text-sm font-medium data-text text-foreground">{building?.occupancy}%</span>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex items-center justify-center gap-1">
                      <Icon
                        name={building?.trend > 0 ? 'TrendingUp' : 'TrendingDown'}
                        size={16}
                        className={building?.trend > 0 ? 'text-success' : 'text-error'}
                      />
                      <span className={`text-sm font-medium data-text ${building?.trend > 0 ? 'text-success' : 'text-error'}`}>
                        {Math.abs(building?.trend)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RankingTable;